import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Button,
  TextField,
  FormControl,
  Select,
  MenuItem,
  InputAdornment,
  Divider,
  Alert,
  Snackbar,
  CircularProgress,
  Autocomplete,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
} from '@mui/material';
import {
  ArrowBack,
  Add as AddIcon,
  Remove,
  Delete,
  Email as EmailIcon,
} from '@mui/icons-material';
import { Layout } from '@layout/Layout';
import { useAppTheme } from '@contexts/ThemeContext';
import {
  createEmployeePayroll,
  getPayrollById,
} from '@/services/HrService/PayrollService';
import { getAllEmployees } from '@/services/HrService/EmployeesService';

interface SalaryComponent {
  id: string;
  label: string;
  amount: number | '';
  percentage: number | '';
  operation: 'add' | 'subtract';
  isPercentage: boolean;
}

const AddEmployeePayroll: React.FC = () => {
  const { payrollId } = useParams<{ payrollId: string }>();
  const navigate = useNavigate();
  const { isDarkMode } = useAppTheme();

  const [loading, setLoading] = useState(false);
  const [payrollInfo, setPayrollInfo] = useState<any>(null);
  const [availableEmployees, setAvailableEmployees] = useState<Array<{ id: string; name: string; email: string }>>([]);
  const [employeeForm, setEmployeeForm] = useState({
    employeeId: '',
    employeeName: '',
    grossSalary: '',
  });
  const [salaryComponents, setSalaryComponents] = useState<SalaryComponent[]>([]);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({
    open: false,
    message: '',
    severity: 'success',
  });

  // Fetch payroll info
  useEffect(() => {
    const fetchPayrollInfo = async () => {
      if (!payrollId) return;

      try {
        const { success, data, error } = await getPayrollById(parseInt(payrollId));

        if (success && data) {
          setPayrollInfo(data);
        } else {
          console.error('Failed to fetch payroll info:', error);
        }
      } catch (err: any) {
        console.error('Error fetching payroll info:', err);
      }
    };

    fetchPayrollInfo();
  }, [payrollId]);

  // Fetch available employees
  useEffect(() => {
    const fetchEmployees = async () => {
      try {
        const { success, data, error } = await getAllEmployees(1, 100, { status: 'active' });

        if (success && data) {
          const employeeList = data.map((emp: any) => ({
            id: emp.id,
            name: emp.name,
            email: emp.email || ''
          }));
          setAvailableEmployees(employeeList);
        } else {
          console.error('Failed to fetch employees:', error);
        }
      } catch (err: any) {
        console.error('Error fetching employees:', err);
      }
    };

    fetchEmployees();
  }, []);

  const calculateComponentValue = (comp: SalaryComponent, gross: number): number => {
    return comp.isPercentage
      ? gross * ((comp.percentage as number) || 0) / 100
      : (comp.amount as number) || 0;
  };

  const calculateNetSalary = (): number => {
    const gross = parseFloat(employeeForm.grossSalary || '0') || 0;
    let net = gross;
    salaryComponents.forEach((c) => {
      const val = calculateComponentValue(c, gross);
      net += c.operation === 'add' ? val : -val;
    });
    return net;
  };

  const handleAddSalaryComponent = () => {
    setSalaryComponents([
      ...salaryComponents,
      {
        id: `comp-${Date.now()}`,
        label: '',
        amount: '',
        percentage: '',
        operation: 'add',
        isPercentage: false,
      },
    ]);
  };

  const handleUpdateSalaryComponent = (
    compId: string,
    field: keyof SalaryComponent,
    value: any
  ) => {
    setSalaryComponents(
      salaryComponents.map((c) => (c.id === compId ? { ...c, [field]: value } : c))
    );
  };

  const handleDeleteSalaryComponent = (compId: string) => {
    setSalaryComponents(salaryComponents.filter((c) => c.id !== compId));
  };

  const handleAddEmployee = async () => {
    const gross = parseFloat(employeeForm.grossSalary || '0');
    if (!employeeForm.employeeId || gross <= 0) {
      setSnackbar({ open: true, message: 'Please fill all required fields', severity: 'error' });
      return;
    }

    setLoading(true);

    try {
      // Prepare salary components for API
      const apiSalaryComponents = salaryComponents.map((c) => ({
        component_name: c.label,
        component_type: c.isPercentage ? 'percentage' : 'fixed',
        value: c.isPercentage ? Number(c.percentage || 0) : Number(c.amount || 0),
        operation: c.operation
      }));

      // Call the API to create employee payroll
      const response = await createEmployeePayroll({
        payroll_id: parseInt(payrollId || '0'),
        employee_id: employeeForm.employeeId,
        gross_salary: gross,
        salary_components: apiSalaryComponents
      });

      if (!response.success) {
        throw new Error(response.error?.message || 'Failed to add employee to payroll');
      }

      setSnackbar({ open: true, message: 'Employee added successfully!', severity: 'success' });

      // Navigate back to payroll details after 1 second
      setTimeout(() => {
        navigate(`/payroll/${payrollId}`);
      }, 1000);
    } catch (error: any) {
      console.error('Error adding employee to payroll:', error);
      setSnackbar({
        open: true,
        message: error.message || 'Failed to add employee to payroll',
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <Container maxWidth="lg">
        <Box sx={{ p: 3 }}>
          {/* Header */}
          <Box sx={{ mb: 3 }}>
            <Button
              startIcon={<ArrowBack />}
              onClick={() => navigate(`/payroll/${payrollId}`)}
              sx={{
                mb: 2,
                color: isDarkMode ? 'rgba(255, 255, 255, 0.87)' : 'inherit'
              }}
            >
              Back to Payroll Details
            </Button>
            <Typography variant="h4" gutterBottom>
              Add Employee to Payroll
            </Typography>
            {payrollInfo && (
              <Typography variant="subtitle1" color="textSecondary">
                {new Date(payrollInfo.start_date).toLocaleString('default', { month: 'long' })} {new Date(payrollInfo.start_date).getFullYear()}
              </Typography>
            )}
          </Box>

          {/* Form Card */}
          <Card>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <Autocomplete
                  fullWidth
                  options={availableEmployees}
                  getOptionLabel={(option) => option.email}
                  value={availableEmployees.find(emp => emp.id === employeeForm.employeeId) || null}
                  onChange={(event, newValue) => {
                    setEmployeeForm({
                      ...employeeForm,
                      employeeId: newValue?.id || '',
                      employeeName: newValue?.name || '',
                    });
                  }}
                  renderOption={(props, option) => (
                    <Box component="li" {...props} key={option.id}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                        <Typography variant="body2" fontWeight="medium">
                          {option.email}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {option.name}
                        </Typography>
                      </Box>
                    </Box>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Select Employee *"
                      placeholder="Type to search by email..."
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                          <>
                            <InputAdornment position="start">
                              <EmailIcon sx={{ color: 'action.active' }} />
                            </InputAdornment>
                            {params.InputProps.startAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                  noOptionsText="No employees available"
                />

                <TextField
                  fullWidth
                  type="number"
                  label="Gross Salary *"
                  value={employeeForm.grossSalary}
                  onChange={(e) => setEmployeeForm({ ...employeeForm, grossSalary: e.target.value })}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">Rs.</InputAdornment>,
                  }}
                />

                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="h6">Salary Components</Typography>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={handleAddSalaryComponent}
                    sx={{
                      color: isDarkMode ? 'rgba(255, 255, 255, 0.87)' : 'inherit',
                      borderColor: isDarkMode ? 'rgba(255, 255, 255, 0.23)' : 'inherit'
                    }}
                  >
                    Add Component
                  </Button>
                </Box>

                {salaryComponents.length > 0 && (
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Name</TableCell>
                          <TableCell width={100}>Type</TableCell>
                          <TableCell width={120}>Value</TableCell>
                          <TableCell width={80}>Operation</TableCell>
                          <TableCell width={100} align="right">Calculated</TableCell>
                          <TableCell width={50} />
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {salaryComponents.map((comp) => (
                          <TableRow key={comp.id}>
                            <TableCell>
                              <TextField
                                size="small"
                                placeholder="e.g., HRA"
                                value={comp.label}
                                onChange={(e) =>
                                  handleUpdateSalaryComponent(comp.id, 'label', e.target.value)
                                }
                                fullWidth
                              />
                            </TableCell>
                            <TableCell>
                              <FormControl size="small" fullWidth>
                                <Select
                                  value={comp.isPercentage ? 'percentage' : 'fixed'}
                                  onChange={(e) =>
                                    handleUpdateSalaryComponent(
                                      comp.id,
                                      'isPercentage',
                                      e.target.value === 'percentage'
                                    )
                                  }
                                >
                                  <MenuItem value="fixed">Rs. Fixed</MenuItem>
                                  <MenuItem value="percentage">% Percent</MenuItem>
                                </Select>
                              </FormControl>
                            </TableCell>
                            <TableCell>
                              <TextField
                                size="small"
                                type="number"
                                value={comp.isPercentage ? comp.percentage : comp.amount}
                                onChange={(e) => {
                                  const val = e.target.value === '' ? '' : parseFloat(e.target.value);
                                  if (e.target.value === '' || val >= 0) {
                                    handleUpdateSalaryComponent(
                                      comp.id,
                                      comp.isPercentage ? 'percentage' : 'amount',
                                      val
                                    );
                                  }
                                }}
                                fullWidth
                              />
                            </TableCell>
                            <TableCell>
                              <FormControl size="small" fullWidth>
                                <Select
                                  value={comp.operation}
                                  onChange={(e) =>
                                    handleUpdateSalaryComponent(comp.id, 'operation', e.target.value as 'add' | 'subtract')
                                  }
                                >
                                  <MenuItem value="add"><AddIcon fontSize="small" sx={{ color: 'success.main' }} /></MenuItem>
                                  <MenuItem value="subtract"><Remove fontSize="small" sx={{ color: 'error.main' }} /></MenuItem>
                                </Select>
                              </FormControl>
                            </TableCell>
                            <TableCell align="right">
                              <Typography
                                variant="caption"
                                color={comp.operation === 'add' ? 'success.main' : 'error.main'}
                              >
                                {comp.operation === 'add' ? '+' : '-'}Rs.
                                {calculateComponentValue(
                                  comp,
                                  parseFloat(employeeForm.grossSalary || '0')
                                ).toFixed(0)}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleDeleteSalaryComponent(comp.id)}
                              >
                                <Delete fontSize="small" />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}

                {/* Salary Summary */}
                <Card variant="outlined" sx={{ bgcolor: isDarkMode ? 'grey.900' : 'grey.50', p: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2" fontWeight="medium">Gross Salary:</Typography>
                    <Typography variant="body2" fontWeight="medium">
                      Rs.{(parseFloat(employeeForm.grossSalary || '0') || 0).toLocaleString()}
                    </Typography>
                  </Box>

                  {salaryComponents.length > 0 && (
                    <>
                      <Divider sx={{ my: 1 }} />
                      {salaryComponents.map((comp) => {
                        const value = calculateComponentValue(comp, parseFloat(employeeForm.grossSalary || '0'));
                        return (
                          <Box key={comp.id} sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                            <Typography
                              variant="body2"
                              color={comp.operation === 'add' ? 'success.main' : 'error.main'}
                            >
                              {comp.label || 'Unnamed Component'}:
                            </Typography>
                            <Typography
                              variant="body2"
                              color={comp.operation === 'add' ? 'success.main' : 'error.main'}
                              fontWeight="medium"
                            >
                              {comp.operation === 'add' ? '+' : '-'}Rs.{value.toLocaleString()}
                            </Typography>
                          </Box>
                        );
                      })}
                    </>
                  )}

                  <Divider sx={{ my: 1 }} />
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="h6">Net Salary:</Typography>
                    <Typography
                      variant="h6"
                      sx={{
                        color: isDarkMode ? '#90caf9' : 'primary.main'
                      }}
                    >
                      Rs.{calculateNetSalary().toLocaleString()}
                    </Typography>
                  </Box>
                </Card>

                {/* Action Buttons */}
                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 2 }}>
                  <Button
                    onClick={() => navigate(`/payroll/${payrollId}`)}
                    disabled={loading}
                    sx={{
                      color: isDarkMode ? 'rgba(255, 255, 255, 0.87)' : 'inherit'
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="contained"
                    onClick={handleAddEmployee}
                    disabled={loading}
                    startIcon={loading ? <CircularProgress size={20} /> : null}
                  >
                    {loading ? 'Adding...' : 'Add Employee'}
                  </Button>
                </Box>
              </Box>
            </CardContent>
          </Card>

          {/* Snackbar */}
          <Snackbar
            open={snackbar.open}
            autoHideDuration={3000}
            onClose={() => setSnackbar({ ...snackbar, open: false })}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          >
            <Alert
              onClose={() => setSnackbar({ ...snackbar, open: false })}
              severity={snackbar.severity}
              variant="filled"
            >
              {snackbar.message}
            </Alert>
          </Snackbar>
        </Box>
      </Container>
    </Layout>
  );
};

export default AddEmployeePayroll;
